import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import { trackPageView } from './utils/metaPixel';
import { trackPageVisit as redditPageVisit } from './utils/redditPixel';
import { captureClickIds } from './utils/adClickIds';

// SPA route-change pinger for ad-attribution pixels. Mounted inside
// <BrowserRouter> so it can subscribe to useLocation(). Without it,
// the pixels only see the first HTML load and every internal navigation
// is invisible.
//
// IMPORTANT: skip the first effect run. The base pixel snippets in
// public/index.html already fire PageView (Meta) and PageVisit (Reddit)
// when the HTML loads — if we also fire here on mount, every initial
// page load gets double-counted. Reddit Pixel Helper visibly shows
// this as "2 pixel events found" when only 1 should fire.
//
// captureClickIds() runs on every navigation (including first) because
// it's idempotent and writes are cheap.
const AdPixelRouteListener: React.FC = () => {
  const location = useLocation();
  const firedFirst = React.useRef(false);
  useEffect(() => {
    captureClickIds();    // Always — first-touch wins, idempotent
    if (!firedFirst.current) {
      firedFirst.current = true;
      return;             // Skip — base snippet already fired
    }
    trackPageView();      // Meta (subsequent SPA navigations)
    redditPageVisit();    // Reddit (subsequent SPA navigations)
  }, [location.pathname, location.search]);
  return null;
};

/* ── Critical pages loaded immediately ───────────────────────────────────── */
import LandingPage   from './pages/LandingPage.tsx';
import LoginPage     from './pages/LoginPage.tsx';
import RegisterPage  from './pages/RegisterPage.tsx';

/* ── Heavy pages code-split — loaded only when navigated to ─────────────── */
const HomePage         = lazy(() => import('./pages/HomePage.tsx'));
const IntelligencePage = lazy(() => import('./pages/IntelligencePage.tsx'));
const BookmarksPage    = lazy(() => import('./pages/BookmarksPage.tsx'));
const WatchlistPage    = lazy(() => import('./pages/WatchlistPage.tsx'));
const PreferencesPage  = lazy(() => import('./pages/PreferencesPage.tsx'));
const BillingPage      = lazy(() => import('./pages/BillingPage.tsx'));
const CheckoutPage     = lazy(() => import('./pages/CheckoutPage.tsx'));
const ResetPasswordPage= lazy(() => import('./pages/ResetPasswordPage.tsx'));
const TrialPage        = lazy(() => import('./pages/TrialPage.tsx'));
const ApiPage          = lazy(() => import('./pages/ApiPage.tsx'));
const OnboardingPage   = lazy(() => import('./pages/OnboardingPage.tsx'));
const NotFoundPage     = lazy(() => import('./pages/NotFoundPage.tsx'));
const TermsPage        = lazy(() => import('./pages/TermsPage.tsx'));
const PrivacyPage      = lazy(() => import('./pages/PrivacyPage.tsx'));
const SourcesPage      = lazy(() => import('./pages/SourcesPage.tsx'));
const CreatorsPage     = lazy(() => import('./pages/CreatorsPage.tsx'));
const BlogPage         = lazy(() => import('./pages/BlogPage.tsx'));
const BlogPostPage     = lazy(() => import('./pages/BlogPostPage.tsx'));

/* ── Page loader shown during lazy-load transitions ─────────────────────── */
const PageLoader = () => (
  <div style={{
    background: '#080808', minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      width: 22, height: 22,
      border: '2px solid rgba(78,222,163,0.3)',
      borderTopColor: '#4EDEA3',
      borderRadius: '50%',
      animation: 'spin 0.75s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

interface User {
  id: string;
  username: string;
  email: string;
  plan?: string;
  trialEndsAt?: string | null;
  onboarded?: boolean;
}

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Verify token + refresh plan from DB. Clears session if token is invalid.
  const verifySession = async (showSpinner = false) => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!storedUser || !token) {
      setLoading(false);
      return;
    }

    let parsed: User;
    try {
      parsed = JSON.parse(storedUser);
    } catch {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setLoading(false);
      return;
    }

    // Optimistically show cached user while we verify
    if (showSpinner) setLoading(true);
    else setUser(parsed);

    try {
      const res = await axios.get('/api/users/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated: User = {
        ...parsed,
        plan:        res.data.plan,
        trialEndsAt: res.data.trialEndsAt ?? null,
        onboarded:   res.data.onboarded ?? parsed.onboarded ?? false,
      };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        // Token expired or invalid — force re-login
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
      }
      // Network error: keep cached user as-is (they're offline, not unauthenticated)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifySession();

    // Re-verify whenever the user returns to this tab (catches stale sessions)
    const onVisible = () => {
      if (!document.hidden) verifySession();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []); // eslint-disable-line

  const handleLogin = (userData: User) => {
    setUser(userData);
  };

  const handleRegister = (userData: User) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  if (loading) {
    return (
      <div style={{
        background: '#0a0a0a', minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 22, height: 22,
          border: '2px solid rgba(78,222,163,0.4)',
          borderTopColor: '#4EDEA3',
          borderRadius: '50%',
          animation: 'spin 0.75s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const content = (
    <BrowserRouter>
      <AdPixelRouteListener />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public landing page — visible before login */}
        <Route path="/" element={<LandingPage />} />
        {/* Markets — the old homepage, now behind auth */}
        <Route
          path="/markets"
          element={user ? <HomePage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/login"
          element={user ? <Navigate to="/intelligence" /> : <LoginPage onLoginSuccess={handleLogin} />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/intelligence" /> : <RegisterPage onRegisterSuccess={handleRegister} />}
        />
        <Route
          path="/bookmarks"
          element={user ? <BookmarksPage user={user} /> : <Navigate to="/login" />}
        />
        <Route
          path="/watchlist"
          element={user ? <WatchlistPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/preferences"
          element={user ? <PreferencesPage /> : <Navigate to="/login" />}
        />
        {/* Billing/Pricing — public so landing page "Pricing" link works before login */}
        <Route path="/billing" element={<BillingPage />} />
        <Route
          path="/checkout"
          element={user ? <CheckoutPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/trial"
          element={user ? <TrialPage user={user} onTrialActivated={(u) => { setUser(u); localStorage.setItem('user', JSON.stringify(u)); }} /> : <Navigate to="/login" />}
        />
        <Route
          path="/onboarding"
          element={
            user
              ? (user.onboarded
                  ? <Navigate to="/intelligence" />
                  : <OnboardingPage user={user} onComplete={(u) => { setUser(u); localStorage.setItem('user', JSON.stringify(u)); }} />)
              : <Navigate to="/login" />
          }
        />
        <Route
          path="/intelligence"
          element={
            user
              ? (user.onboarded === false
                  ? <Navigate to="/onboarding" />
                  : <IntelligencePage user={user} onLogout={handleLogout} />)
              : <Navigate to="/login" />
          }
        />
        <Route
          path="/api"
          element={user ? <ApiPage /> : <Navigate to="/login" />}
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/terms"           element={<TermsPage />} />
        <Route path="/privacy"         element={<PrivacyPage />} />
        <Route path="/sources"         element={<SourcesPage />} />
        <Route path="/creators"        element={<CreatorsPage />} />
        <Route path="/blog"            element={<BlogPage />} />
        <Route path="/blog/:slug"      element={<BlogPostPage />} />
        <Route path="*"                element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );

  return (
    <ErrorBoundary>
      {googleClientId
        ? <GoogleOAuthProvider clientId={googleClientId}>{content}</GoogleOAuthProvider>
        : content}
    </ErrorBoundary>
  );
};

export default App;
